// @ts-nocheck
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  Search,
  SortAsc,
  Grid,
  List,
  Clock,
  Calendar,
  Sparkles,
  ChevronDown,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import ActivityCard from '../../components/ActivityCard/ActivityCard';
import {
  SearchFiltersDesktop,
  SearchFiltersMobile,
  defaultFilters,
} from '../../components/SearchFilters/SearchFilters';
import type { SearchFiltersState } from '../../components/SearchFilters/SearchFilters';
import {
  fetchSearchResults,
  selectSearchActivities,
  selectSearchNeighborhoods,
  selectSearchInProgress,
} from './SearchPageView.duck';
import { Page } from '../../components';
import TopbarContainerStyled from '../TopbarContainer/TopbarContainerStyled';
import FooterStyled from '../FooterContainer/FooterStyled';

import '../../styles/tailwind.css';

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const fullDayNames = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const timePresets = [
  {
    labelKey: 'SearchPageView.timeMorning',
    descKey: 'SearchPageView.timeMorningDesc',
    value: 'morning',
    startTime: '06:00',
    endTime: '12:00',
  },
  {
    labelKey: 'SearchPageView.timeAfternoon',
    descKey: 'SearchPageView.timeAfternoonDesc',
    value: 'afternoon',
    startTime: '12:00',
    endTime: '17:00',
  },
  {
    labelKey: 'SearchPageView.timeEvening',
    descKey: 'SearchPageView.timeEveningDesc',
    value: 'evening',
    startTime: '17:00',
    endTime: '21:00',
  },
];

const quickFilters = [
  {
    labelKey: 'SearchPageView.quickFilterWeekdayAfternoons',
    days: [1, 2, 3, 4, 5],
    time: 'afternoon',
  },
  {
    labelKey: 'SearchPageView.quickFilterSaturdayMornings',
    days: [6],
    time: 'morning',
  },
  {
    labelKey: 'SearchPageView.quickFilterSundayAfternoons',
    days: [0],
    time: 'afternoon',
  },
  {
    labelKey: 'SearchPageView.quickFilterAfterSchool',
    days: [1, 2, 3, 4, 5],
    time: 'evening',
  },
];

function getInitialSearchParams() {
  const searchParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : ''
  );
  const singleDay = searchParams.get('day');
  const multipleDays =
    searchParams
      .get('days')
      ?.split(',')
      .map(d => parseInt(d, 10))
      .filter(d => !isNaN(d)) || [];
  const days = singleDay
    ? [parseInt(singleDay, 10)].filter(d => !isNaN(d))
    : multipleDays;
  const startTime = searchParams.get('startTime') || '';
  const endTime = searchParams.get('endTime') || '';

  let timePresetVal: string | null = null;
  if (startTime && endTime) {
    const preset = timePresets.find(
      t => t.startTime === startTime && t.endTime === endTime
    );
    if (preset) timePresetVal = preset.value;
  }

  return {
    query: searchParams.get('q') || '',
    category: searchParams.get('category') || '',
    days,
    startTime,
    endTime,
    timePreset: timePresetVal,
    age: searchParams.get('age')
      ? parseInt(searchParams.get('age')!, 10)
      : null,
  };
}

const SearchPageView = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const initial = getInitialSearchParams();

  const activities = useSelector(selectSearchActivities);
  const neighborhoods = useSelector(selectSearchNeighborhoods);
  const isLoading = useSelector(selectSearchInProgress);

  const [searchQuery, setSearchQuery] = useState(initial.query);
  const [selectedDays, setSelectedDays] = useState<number[]>(initial.days);
  const [selectedTimePreset, setSelectedTimePreset] = useState<string | null>(
    initial.timePreset
  );
  const [customStartTime, setCustomStartTime] = useState(
    initial.timePreset ? '' : initial.startTime
  );
  const [customEndTime, setCustomEndTime] = useState(
    initial.timePreset ? '' : initial.endTime
  );
  const [filters, setFilters] = useState<SearchFiltersState>({
    ...defaultFilters,
    categories: initial.category ? [initial.category] : [],
    age: initial.age,
  });
  const [sortBy, setSortBy] = useState<string>('best_fit');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showQuickFilters, setShowQuickFilters] = useState(true);
  const [quickFilterActive, setQuickFilterActive] = useState(false);
  const hasAutoCollapsed = useRef(false);

  const timePreset = timePresets.find(t => t.value === selectedTimePreset);

  // Build query params and dispatch search
  const queryParams = useMemo(() => {
    const params: Record<string, string> = {};
    const combinedDays = Array.from(new Set([...selectedDays, ...filters.days])).sort();

    if (combinedDays.length === 1) params.day = combinedDays[0].toString();
    if (combinedDays.length > 1) params.days = combinedDays.join(',');
    if (timePreset) {
      params.startTime = timePreset.startTime;
      params.endTime = timePreset.endTime;
    } else if (customStartTime && customEndTime) {
      params.startTime = customStartTime;
      params.endTime = customEndTime;
    } else if (filters.timeSlots.length > 0) {
      params.timeSlots = filters.timeSlots.join(',');
    }
    if (searchQuery) params.q = searchQuery;
    if (filters.categories.length > 0)
      params.categories = filters.categories.join(',');
    if (filters.neighborhoods.length > 0)
      params.neighborhoods = filters.neighborhoods.join(',');
    if (filters.verifiedOnly) params.verifiedOnly = 'true';
    if (filters.bookableOnly) params.bookableOnly = 'true';
    if (filters.skillLevels.length > 0)
      params.skillLevels = filters.skillLevels.join(',');
    if (filters.indoorOutdoor.length > 0)
      params.indoorOutdoor = filters.indoorOutdoor.join(',');
    if (filters.age !== null) params.age = filters.age.toString();
    if (filters.priceMin > 0) params.priceMin = filters.priceMin.toString();
    if (filters.priceMax < 500) params.priceMax = filters.priceMax.toString();
    if (sortBy) params.sortBy = sortBy;
    return params;
  }, [
    selectedDays,
    timePreset,
    customStartTime,
    customEndTime,
    searchQuery,
    filters,
    sortBy,
  ]);

  useEffect(() => {
    dispatch(fetchSearchResults(queryParams) as any);
  }, [dispatch, queryParams]);

  // Update URL
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedDays.length === 1) {
      params.set('day', selectedDays[0].toString());
    } else if (selectedDays.length > 1) {
      params.set('days', selectedDays.join(','));
    }
    if (selectedTimePreset) {
      const preset = timePresets.find(t => t.value === selectedTimePreset);
      if (preset) {
        params.set('startTime', preset.startTime);
        params.set('endTime', preset.endTime);
      }
    } else if (customStartTime && customEndTime) {
      params.set('startTime', customStartTime);
      params.set('endTime', customEndTime);
    }
    if (filters.age !== null) params.set('age', filters.age.toString());
    if (filters.categories.length > 0)
      params.set('category', filters.categories[0]);
    const newUrl = params.toString() ? `/s?${params.toString()}` : '/s';
    window.history.replaceState({}, '', newUrl);
  }, [
    searchQuery,
    selectedDays,
    selectedTimePreset,
    customStartTime,
    customEndTime,
    filters.age,
    filters.categories,
  ]);

  const hasTimeFilters =
    selectedDays.length > 0 ||
    selectedTimePreset !== null ||
    (customStartTime && customEndTime);

  // Auto-collapse quick filters once user makes a selection
  useEffect(() => {
    if (hasTimeFilters && !hasAutoCollapsed.current) {
      hasAutoCollapsed.current = true;
      setShowQuickFilters(false);
    }
    if (!hasTimeFilters) {
      hasAutoCollapsed.current = false;
    }
  }, [hasTimeFilters]);

  // Client-side sorting for fields Meilisearch doesn't handle
  const sortedActivities = useMemo(() => {
    const result = [...activities];
    if (sortBy === 'price_low') {
      result.sort(
        (a, b) =>
          (Number(a.priceAmountCents) || 0) - (Number(b.priceAmountCents) || 0)
      );
    } else if (sortBy === 'price_high') {
      result.sort(
        (a, b) =>
          (Number(b.priceAmountCents) || 0) - (Number(a.priceAmountCents) || 0)
      );
    } else if (sortBy === 'name_asc') {
      result.sort((a, b) => a.title.localeCompare(b.title));
    }
    return result;
  }, [activities, sortBy]);

  const handleQuickFilter = useCallback(
    (quick: (typeof quickFilters)[0]) => {
      setSelectedDays(quick.days);
      setSelectedTimePreset(quick.time);
      setQuickFilterActive(true);
    },
    []
  );

  const toggleDay = useCallback((dayIndex: number) => {
    setSelectedDays(prev =>
      prev.includes(dayIndex)
        ? prev.filter(d => d !== dayIndex)
        : [...prev, dayIndex]
    );
  }, []);

  const clearTimeFilters = useCallback(() => {
    setSelectedDays([]);
    setSelectedTimePreset(null);
    setCustomStartTime('');
    setCustomEndTime('');
    setQuickFilterActive(false);
  }, []);

  const getSelectedDaysText = () => {
    if (selectedDays.length === 0) return null;
    if (selectedDays.length === 1) return fullDayNames[selectedDays[0]];
    if (
      selectedDays.length === 5 &&
      [1, 2, 3, 4, 5].every(d => selectedDays.includes(d))
    )
      return intl.formatMessage({ id: 'SearchPageView.weekdays' });
    if (
      selectedDays.length === 2 &&
      selectedDays.includes(0) &&
      selectedDays.includes(6)
    )
      return intl.formatMessage({ id: 'SearchPageView.weekends' });
    return selectedDays.map(d => dayNames[d]).join(', ');
  };

  const title = intl.formatMessage({ id: 'SearchPageView.title' });

  return (
    <Page title={title} scrollingDisabled={false}>
      <TopbarContainerStyled currentPage="SearchPage" />
      <div className="styled-page">
        <div className="bg-gradient-to-b from-primary/5 to-background">
          <div className="container px-4 md:px-6 py-6 md:py-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-3 py-1 text-sm font-medium mb-3">
                <Clock className="h-3.5 w-3.5" />
                <span>
                  {intl.formatMessage({ id: 'SearchPageView.searchBySchedule' })}
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2">
                {intl.formatMessage({ id: 'SearchPageView.heading' })}
              </h1>
              <p className="text-muted-foreground" style={{ textAlign: 'center', maxWidth: '36rem', margin: '0 auto' }}>
                {intl.formatMessage({ id: 'SearchPageView.subheading' })}
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-4">
              {/* Quick filters — collapsible */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowQuickFilters(v => !v)}
                  className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-2 group"
                >
                  <Sparkles className="h-4 w-4" />
                  {intl.formatMessage({ id: 'SearchPageView.popularTimeSlots' })}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-300 ${showQuickFilters ? '' : '-rotate-90'}`}
                  />
                </button>
                <div
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                  style={{ maxHeight: showQuickFilters ? '200px' : '0px' }}
                >
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pb-1">
                    {quickFilters.map(quick => {
                      const isActive =
                        quick.days.length === selectedDays.length &&
                        quick.days.every(d => selectedDays.includes(d)) &&
                        quick.time === selectedTimePreset;
                      const matchedPreset = timePresets.find(
                        t => t.value === quick.time
                      );
                      return (
                        <Button
                          key={quick.labelKey}
                          variant={isActive ? 'default' : 'outline'}
                          className="h-auto py-2 flex flex-col items-center gap-0.5 text-sm"
                          onClick={() => handleQuickFilter(quick)}
                        >
                          <span className="font-medium leading-tight">
                            {intl.formatMessage({ id: quick.labelKey })}
                          </span>
                          <span className="text-xs opacity-60 leading-tight">
                            {quick.days.length > 1
                              ? intl.formatMessage({ id: 'SearchPageView.quickFilterMonFri' })
                              : dayNames[quick.days[0]]}
                            {', '}
                            {matchedPreset
                              ? intl.formatMessage({ id: matchedPreset.descKey })
                              : ''}
                          </span>
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Day + Time selectors — collapse when a quick filter is active */}
              <div
                className="overflow-hidden transition-all duration-300 ease-in-out"
                style={{ maxHeight: quickFilterActive ? '0px' : '600px', opacity: quickFilterActive ? 0 : 1 }}
              >
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-primary" />
                        {intl.formatMessage({
                          id: 'SearchPageView.selectDays',
                        })}
                      </h3>
                      {selectedDays.length > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {intl.formatMessage(
                            { id: 'SearchPageView.daysSelected' },
                            { count: selectedDays.length }
                          )}
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-7 gap-2">
                      {dayNames.map((day, index) => (
                        <Button
                          key={day}
                          variant={
                            selectedDays.includes(index) ? 'default' : 'outline'
                          }
                          className="h-12 p-0 flex flex-col"
                          onClick={() => toggleDay(index)}
                        >
                          <span className="text-xs opacity-70">{day}</span>
                        </Button>
                      ))}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedDays([1, 2, 3, 4, 5])}
                        className="text-xs"
                      >
                        {intl.formatMessage({ id: 'SearchPageView.weekdays' })}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedDays([0, 6])}
                        className="text-xs"
                      >
                        {intl.formatMessage({ id: 'SearchPageView.weekends' })}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Clock className="h-5 w-5 text-primary" />
                        {intl.formatMessage({
                          id: 'SearchPageView.selectTime',
                        })}
                      </h3>
                    </div>
                    <div className="space-y-2">
                      {timePresets.map(preset => (
                        <Button
                          key={preset.value}
                          variant={
                            selectedTimePreset === preset.value
                              ? 'default'
                              : 'outline'
                          }
                          className="w-full justify-between h-12"
                          onClick={() =>
                            setSelectedTimePreset(
                              selectedTimePreset === preset.value
                                ? null
                                : preset.value
                            )
                          }
                        >
                          <span className="font-medium">
                            {intl.formatMessage({ id: preset.labelKey })}
                          </span>
                          <span className="text-sm opacity-70">
                            {intl.formatMessage({ id: preset.descKey })}
                          </span>
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
              </div>

              {/* Active filter summary */}
              {hasTimeFilters && (
                <div className="flex items-center justify-between bg-primary/5 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {intl.formatMessage({
                          id: 'SearchPageView.searchingFor',
                        })}
                      </p>
                      <p className="font-medium">
                        {getSelectedDaysText() ||
                          intl.formatMessage({ id: 'SearchPageView.anyDay' })}
                        {timePreset &&
                          ` ${intl.formatMessage(
                            { id: 'SearchPageView.duringTime' },
                            {
                              time: intl
                                .formatMessage({ id: timePreset.labelKey })
                                .toLowerCase(),
                              description: intl.formatMessage({
                                id: timePreset.descKey,
                              }),
                            }
                          )}`}
                        {!timePreset &&
                          customStartTime &&
                          customEndTime &&
                          ` ${intl.formatMessage(
                            { id: 'SearchPageView.fromTimeTo' },
                            {
                              startTime: customStartTime,
                              endTime: customEndTime,
                            }
                          )}`}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearTimeFilters}
                  >
                    {intl.formatMessage({ id: 'SearchPageView.clear' })}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Results section */}
        <div className="container px-4 md:px-6 py-6">
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder={intl.formatMessage({
                    id: 'SearchPageView.searchPlaceholder',
                  })}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                <SearchFiltersMobile
                  filters={filters}
                  onFiltersChange={setFilters}
                  neighborhoods={neighborhoods}
                />
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px]">
                    <SortAsc className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="best_fit">
                      {intl.formatMessage({
                        id: 'SearchPageView.sortBestMatch',
                      })}
                    </SelectItem>
                    <SelectItem value="price_low">
                      {intl.formatMessage({
                        id: 'SearchPageView.sortPriceLow',
                      })}
                    </SelectItem>
                    <SelectItem value="price_high">
                      {intl.formatMessage({
                        id: 'SearchPageView.sortPriceHigh',
                      })}
                    </SelectItem>
                    <SelectItem value="name_asc">
                      {intl.formatMessage({
                        id: 'SearchPageView.sortNameAZ',
                      })}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <div className="hidden sm:flex border rounded-md">
                  <Button
                    variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="icon"
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                    size="icon"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              {isLoading
                ? intl.formatMessage({ id: 'SearchPageView.loading' })
                : intl.formatMessage(
                    { id: 'SearchPageView.resultCount' },
                    { count: sortedActivities.length }
                  )}
            </p>
          </div>

          <div className="flex gap-6">
            <SearchFiltersDesktop
              filters={filters}
              onFiltersChange={setFilters}
              neighborhoods={neighborhoods}
            />

            <div className="flex-1">
              {isLoading ? (
                <div
                  className={
                    viewMode === 'grid'
                      ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'
                      : 'space-y-4'
                  }
                >
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-64 w-full" />
                  ))}
                </div>
              ) : sortedActivities.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">
                    {intl.formatMessage({
                      id: 'SearchPageView.noResults',
                    })}
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => {
                      setFilters(defaultFilters);
                      clearTimeFilters();
                    }}
                  >
                    {intl.formatMessage({
                      id: 'SearchPageView.clearAllFilters',
                    })}
                  </Button>
                </div>
              ) : (
                <div
                  className={
                    viewMode === 'grid'
                      ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'
                      : 'space-y-4'
                  }
                >
                  {sortedActivities.map(activity => (
                    <ActivityCard key={activity.id} activity={activity} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <FooterStyled />
    </Page>
  );
};

export default SearchPageView;
